// Maps an oidc-client-ts User (claims from Keycloak's validated ID token) onto the
// session source object accessControl.js has always consumed.

const OIDC_ROLE_ALIASES = Object.freeze({
  'system-administrator': 'admin',
  'platform-admin': 'admin',
  'platform-administrator': 'admin',
  adviser: 'advisor',
  'relationship-manager': 'advisor',
  relationship_manager: 'advisor',
  'business-admin': 'advisor',
  'business-administrator': 'advisor',
  'advisor-firm-admin': 'advisor',
  'practice-admin': 'advisor',
});

function canonicalOidcRole(role) {
  const value = String(role || '').trim().toLowerCase();
  return OIDC_ROLE_ALIASES[value] || value;
}

function accessTokenClaims(accessToken) {
  const token = String(accessToken || '');
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1] || parts[1].length > 65536) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(parts[1].length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
    const value = JSON.parse(new TextDecoder().decode(bytes));
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
  } catch (_error) {
    return null;
  }
}

// Fixed priority (the order of knownRoles: admin > advisor > parent > kid), not claim order.
export function pickAppRole(roles, knownRoles) {
  const granted = new Set((Array.isArray(roles) ? roles : []).map(canonicalOidcRole));
  return knownRoles.find(role => granted.has(canonicalOidcRole(role))) || '';
}

export function buildOidcSessionSource(user, knownRoles) {
  const profile = user?.profile;
  if (!profile) return null;
  // The API authorises the OAuth access token, not the ID token. Keycloak can map
  // realm roles differently into those two tokens, so frontend navigation must use
  // the same access-token claims or it can expose an Administration shell the API
  // correctly refuses. Opaque or malformed tokens retain the existing ID-token
  // fallback for compatibility; the backend remains the enforcement boundary.
  const accessClaims = accessTokenClaims(user.access_token);
  const authorizationClaims = accessClaims || profile;
  const scope = authorizationClaims.customer_scope ?? profile.customer_scope;
  const expiresAt = Number(user.expires_at);
  return {
    userId: String(profile.sub || ''),
    userEmail: String(profile.email || ''),
    displayName: String(profile.name || profile.preferred_username || profile.sub || ''),
    role: pickAppRole(authorizationClaims.realm_access?.roles, knownRoles),
    familyId: String(authorizationClaims.family_id || profile.family_id || ''),
    familyMemberId: String(authorizationClaims.family_member_id || profile.family_member_id || ''),
    assignedFamilyIds: (Array.isArray(scope) ? scope : scope ? [scope] : []).map(String),
    accessToken: String(user.access_token || ''),
    sessionExpiresAt: Number.isFinite(expiresAt) ? new Date(expiresAt * 1000).toISOString() : null,
    // A refresh token can renew an expired access token without a redirect.
    renewable: Boolean(user.refresh_token),
    authMode: 'jwt',
    authSource: 'keycloak',
  };
}

// Session flags derived from a session source:
// - accessTokenExpired: the access token's lifetime has run out;
// - sessionExpired: expired and nothing can renew it (no refresh token);
// - roleMissing: a Keycloak user is loaded but has no Mitus role;
// - sessionActive: usable, including "expired but renewable" (the next API 401 renews it).
export function describeSessionState(source, now = Date.now()) {
  if (!source) return { accessTokenExpired: false, renewable: false, sessionExpired: false, roleMissing: false, sessionActive: false };
  const expiry = Date.parse(source.sessionExpiresAt || '');
  const accessTokenExpired = Number.isFinite(expiry) && expiry <= now;
  const renewable = Boolean(source.renewable);
  const sessionExpired = accessTokenExpired && !renewable;
  const roleMissing = !source.role;
  return { accessTokenExpired, renewable, sessionExpired, roleMissing, sessionActive: !sessionExpired && !roleMissing };
}

// Logical login identity: a renewed token keeps the key; a different user, role or scope changes it.
export function sessionIdentityKey(source) {
  return JSON.stringify([source.userId, source.role, source.familyId, source.familyMemberId, source.assignedFamilyIds]);
}
